import React from "react";
import { DailyTip } from "@/features/daily/components/DailyTip";
import { useDailyNow } from "@/features/daily/hooks/use-daily-now";
import { CauseCountsVm, StatusPillVm, healthViewModel } from "@/features/daily/components/command/health-view-model";

type Props = {
    fetchedAt: number | null;
    auto: boolean;
    onToggleAuto: () => void;
    pollSeconds: number;
    /** `null` until the first status arrives. */
    pill: StatusPillVm | null;
    counts: CauseCountsVm | null;
};

const madridClock = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});

const partOf = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((piece) => piece.type === type);
    return part === undefined ? "" : part.value;
};

/**
 * D125: the live bar of Salud — refresh age and auto-refresh on the left, what moves
 * the state in the centre, the Madrid clock on the right. Not a live region: nothing
 * here is announced every second; the seconds are hidden from assistive technology.
 */
export const LiveBar: React.FC<Props> = ({ fetchedAt, auto, onToggleAuto, pollSeconds, pill, counts }) => {
    const now = useDailyNow(1000);
    const clock = madridClock.formatToParts(new Date(now));
    return (
        <div className="daily-live" role="group" aria-label="Estado en vivo">
            <div className="daily-live__side">
                <span className="daily-live__badge" data-live={auto}>
                    <span className="daily-dot" aria-hidden="true" />
                    {auto ? "EN VIVO" : "EN PAUSA"}
                </span>
                <span className="daily-live__age">Último refresco {healthViewModel.refreshedAgo(fetchedAt, now)}</span>
                {/* Guardian P3: a toggle keeps one name; `aria-pressed` and the icon carry the state. */}
                <button type="button" className="daily-live__toggle" aria-pressed={auto} title={`Cada ${pollSeconds} s`} onClick={onToggleAuto}>
                    <span aria-hidden="true">{auto ? "▶" : "❚❚"}</span> Actualización automática
                </button>
            </div>
            <div className="daily-live__centre">
                {pill === null ? null : (
                    <span className="daily-pill daily-live__state" data-tone={pill.tone}>
                        <span className="daily-dot" aria-hidden="true" />
                        {pill.label}
                        <DailyTip id="daily-live-state-tip" label="Detalle del estado" lines={pill.tooltip} />
                    </span>
                )}
                {counts === null || counts.critical === 0 ? null : (
                    <span className="daily-pill" data-tone="critical">CRÍTICO {counts.critical}</span>
                )}
                {counts === null || counts.warning === 0 ? null : (
                    <span className="daily-pill" data-tone="warning">AVISO {counts.warning}</span>
                )}
            </div>
            <div className="daily-live__side daily-live__clock">
                <span className="daily-live__zone">MAD</span>
                <time className="daily-live__time">
                    {partOf(clock, "hour")}:{partOf(clock, "minute")}
                    <span aria-hidden="true">:{partOf(clock, "second")}</span>
                </time>
            </div>
        </div>
    );
};
